import { Button } from '@/components/ui/Button';
import React, { useEffect, useRef, useState } from 'react';
import { Separator } from '../Separator';

export type Time = {
  hour: number;
  minute: number;
  second?: number;
};

const Padding = () => (
  <div className="flex flex-col gap-1">
    <div className="h-8">&nbsp;</div>
    <div className="h-8">&nbsp;</div>
    <div className="h-8">&nbsp;</div>
    <div className="h-8">&nbsp;</div>
  </div>
);

export const TimePicker: React.FC<{
  time?: Time;
  onTimeChange: (time: Time) => void;
}> = ({ time, onTimeChange }) => {
  const initTime = useRef<boolean>(true);
  const hours = Array.from({ length: 24 }, (_, i) => i);
  const minutes = Array.from({ length: 60 }, (_, i) => i);
  const seconds = Array.from({ length: 60 }, (_, i) => i);
  const [pickedTime, setPickedTime] = useState<Time>({
    hour: 0,
    minute: 0,
    second: 0,
  });

  useEffect(() => {
    if (time) {
      setPickedTime({
        hour: time.hour,
        minute: time.minute,
        second: time.second,
      });
      if (initTime.current) {
        scrollTo('hour', time.hour, 'instant');
        scrollTo('minute', time.minute, 'instant');
        if (time.second !== undefined) {
          scrollTo('second', time.second, 'instant');
        }
        initTime.current = false;
      }
    }
  }, [time]);

  useEffect(() => {
    if (pickedTime) {
      const { hour, minute, second } = pickedTime;
      scrollTo('hour', hour);
      scrollTo('minute', minute);
      if (second !== undefined) {
        scrollTo('second', second);
      }
    }
  }, [pickedTime]);

  const handleHourChange = (val: number) => {
    setPickedTime({ ...pickedTime, hour: val });
  };

  const handleMinuteChange = (val: number) => {
    setPickedTime({ ...pickedTime, minute: val });
  };

  const handleSecondChange = (val: number) => {
    setPickedTime({ ...pickedTime, second: val });
  };

  const scrollTo = (
    unit: 'hour' | 'minute' | 'second',
    value: number,
    behavior: ScrollBehavior = 'smooth',
  ) => {
    const target = document.getElementById(`${unit}-${value}`);
    if (target) {
      target.scrollIntoView({ behavior, block: 'start' });
    }
  };

  const handleNowClick = () => {
    const now = new Date();
    setPickedTime({
      hour: now.getHours(),
      minute: now.getMinutes(),
      second: now.getSeconds(),
    });
  };

  return (
    <div className="flex flex-col gap-1">
      <div className="flex">
        <div className="me-2 flex h-44 flex-col gap-1 overflow-hidden px-1 hover:me-0 hover:overflow-x-hidden hover:overflow-y-scroll">
          {hours.map((h, index) => {
            return (
              <Button
                className="size-8"
                id={`hour-${index}`}
                variant={pickedTime.hour === h ? 'default' : 'ghost'}
                key={index}
                size={'sm'}
                onClick={() => handleHourChange(h)}
              >
                {h.toString().padStart(2, '0')}
              </Button>
            );
          })}
          <Padding />
        </div>
        <Separator orientation="vertical" />

        <div className="me-2 flex h-44 flex-col gap-1 overflow-hidden px-1 hover:me-0 hover:overflow-x-hidden hover:overflow-y-scroll">
          {minutes.map((m, index) => {
            return (
              <Button
                className="size-8"
                id={`minute-${index}`}
                variant={pickedTime.minute === m ? 'default' : 'ghost'}
                key={index}
                size={'sm'}
                onClick={() => handleMinuteChange(m)}
              >
                {m.toString().padStart(2, '0')}
              </Button>
            );
          })}
          <Padding />
        </div>
        <Separator orientation="vertical" />
        <div className="me-2 flex h-44 flex-col gap-1 overflow-hidden px-1 hover:me-0 hover:overflow-x-hidden hover:overflow-y-scroll">
          {seconds.map((s, index) => {
            return (
              <Button
                className="size-8"
                id={`second-${index}`}
                size={'sm'}
                onClick={() => handleSecondChange(s)}
                variant={pickedTime.second === s ? 'default' : 'ghost'}
                key={index}
              >
                {s.toString().padStart(2, '0')}
              </Button>
            );
          })}
          <Padding />
        </div>
      </div>
      <Separator />
      <div className="flex justify-between">
        <Button size={'sm'} variant={'outline'} onClick={handleNowClick}>
          Now
        </Button>
        <Button
          size={'sm'}
          variant={'outline'}
          onClick={() => onTimeChange(pickedTime)}
        >
          Confirm
        </Button>
      </div>
    </div>
  );
};
